import React from 'react';
import styled from 'styled-components/macro';
import { SimpleFlexContainer } from '../layouts/ng-layouts/Containers';
import Image from '../Image';
import getLogoSrc from '../../utils/getLogoSrc';
import useTranslations from '../../hooks/useTranslations';
import currentDateObject from '../../utils/currentDateObject';
import {
  Tooltip,
  IconButton,
  TextField,
  Button as MuiButton,
  Checkbox,
  FormControlLabel
} from '@material-ui/core';
import Button from '../Button';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import Colors from '../../internal-components/Colors';
import { RelativeLoader } from '../Loader';
import SwipeableViews from 'react-swipeable-views';
import MuiVisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ErrorIcon from '@material-ui/icons/Error';
import Animation from '../Animation';
import ResetPasswordSuggestionDialog from '../../internal-components/GeneralDialog';
import StrengthMeter from '../StrengthMeter';
import { STEPS } from '../engine/LoginPageEngine';
import MultiFactorAuthSetupDialogEngine from '../engine/MultiFactorAuthSetupDialogEngine';
import { Check } from '@material-ui/icons';
import InfoIcon from '@material-ui/icons/Info';

const Background = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  background: rgb(163, 192, 255);
  background: radial-gradient(
    circle,
    rgb(118 161 255) 0%,
    rgba(76, 132, 255, 1) 100%
  );
`;

const Container = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  width: ${({ $mobileView }) => ($mobileView ? '100%' : '40rem')};
  ${({ $mobileView }) => $mobileView && 'height: 100%;'}
  background: #fff;

  border-radius: ${({ $mobileView }) => ($mobileView ? '0px' : '10px')};
`;

const ContainerContent = styled(SimpleFlexContainer)`
  padding: 2rem;
  height: 100%;
`;

const LogoImage = styled.img`
  height: 80px;
  width: auto;
  margin: auto;
`;

const TitleContainer = styled.div`
  h1 {
    margin-top: 1rem;
    margin-bottom: 0.4rem;
  }
`;

const SubTitleContainer = styled.div`
  margin-bottom: 1.5rem;
  h2 {
    font-size: 16px;
  }
`;

const MFATitleContainer = styled.div`
  max-width: 400px;
  margin: 0 auto 16px auto;
  text-align: left;

  h3 {
    color: #1e202d;
    font-size: 15px;
    font-weight: 500;
    margin: 0 0 4px 0;
  }

  h4 {
    color: #1e202d;
    font-size: 11px;
    font-weight: 400;
    margin: 0;
  }
`;

const MFARecoveryCodeInstructionsContainer = styled.div`
  display: flex;
  align-items: start;
  gap: 6px;
`;

const MFARecoveryCodeInstructions = styled.div`
  color: #1e202d;
  font-size: 11px;
  font-weight: 400;
  text-align: left;
`;

const MFATextField = styled(TextField)`
  .MuiInputBase-input {
    font-size: 14px;
    padding: 8px;
  }
`;

const VinciWorksImage = styled(Image)`
  height: 40px;
  //   margin: 1rem auto;
`;

const MetaData = styled.div`
  div {
    margin-top: 0.5rem;
    font-size: 10px;
  }
`;

const FormContainer = styled(SimpleFlexContainer)`
  padding: 2rem 0;
  flex: 1;
  justify-content: center;
`;

const StepContainer = styled(SimpleFlexContainer)`
  min-height: 255px;
  justify-content: ${({ $justify }) => ($justify ? $justify : 'flex-start')};
  overflow: hidden;
`;

const FormBox = styled.div`
  ${({ $mobileView, $mfaView }) =>
    $mobileView ? '' : $mfaView ? 'padding: 0 6rem;' : 'padding: 0 8rem;'}
`;

const ButtonContainer = styled(SimpleFlexContainer)`
  flex-direction: column;
  button {
    width: 100%;
  }
`;

const confirmBtnCss = `
    margin: 1rem 0 0 0 !important;
`;

const VisibilityIcon = styled(MuiVisibilityIcon)`
  color: ${Colors.PRIMARY};
`;

const ButtonLink = styled(MuiButton)`
  && {
    text-transform: none;
    color: ${Colors.LINKS};
    margin: 1rem auto 0 0 !important;
    ${({ $widthAuto }) => ($widthAuto ? 'padding: 0; width: auto;' : '')}
  }
`;

const EmailButton = styled(MuiButton)`
  && {
    text-transform: none;
    color: ${Colors.LINKS};
    margin: 0 !important;
  }
`;

const WelcomeH2 = styled.h2`
  margin: 0;
`;

const EmailContainer = styled.span`
  font-weight: 600;
  color: ${Colors.PRIMARY};
  margin: 0.8rem;
`;

const ErrorLine = styled.small`
  color: ${Colors.OT_RED};
  min-height: 20px;
  display: flex;

  div[data-id='animation-container'] {
    align-items: center;
    display: flex;
  }

  svg {
    margin-right: 0.4rem;
    width: 11px;
    height: 11px;
  }
`;

const VersionContainer = styled.div`
  position: fixed;
  color: white;
  bottom: -1.5rem;
  left: 10px;
`;

const SetPwdReq = styled.small`
  margin-top: 1rem;
`;

const FormControlLabelText = styled.span`
  && {
    font-size: 13px;
  }
`;

const StyledInfoIcon = styled(InfoIcon)`
  && {
    color: #4c84ff;
    font-size: 12px;
    margin: 0;
    vertical-align: sub;
  }
`;

const MFACheckbox = styled(FormControlLabel)`
  && {
    margin: 4px 0;
    margin-left: -11px;
    align-self: flex-start;

    .MuiCheckbox-root {
      padding: 9px 9px 9px 0;
    }

    .MuiFormControlLabel-label {
      font-size: 14px;
    }
  }
`;

const MFAInfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0 8px 0;
  font-size: 11px;
  color: #1e202d;
  line-height: 1.2;

  span {
    text-align: justify;
  }
`;

const LoginPageView = props => {
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();

  return (
    <Background>
      <Container $mobileView={isMobileView}>
        <RelativeLoader
          isLoading={props.isLoading}
          customCSS="border-radius: 10px;"
        >
          <ContainerContent $mobileView={isMobileView}>
            <LogoImage
              src={getLogoSrc()}
              alt="company-logo"
              onError={() => {
                const origin = window.location.origin;
                window.location.href = origin + '/no_tenant';
              }}
            />
            {!window.location.host.startsWith('aml') && (
              <TitleContainer>
                <h1>{t('login.omnitrack')}</h1>
                {t('login.tagline')}
              </TitleContainer>
            )}

            <FormContainer>
              <Animation
                type="collapse"
                in={
                  !props.isResetPasswordEmailSent &&
                  !['MFA', 'RECOVERY_CODE'].includes(props.step)
                }
              >
                <SwipeableViews index={props.step}>
                  <EmailStep {...props} />
                  <PasswordStep {...props} />
                  <ActivateAccountStep {...props} />
                  <SetPasswordStep {...props} />
                </SwipeableViews>
              </Animation>
              <Animation
                type="collapse"
                mountOnEnter
                unmountOnExit
                in={props.isResetPasswordEmailSent}
              >
                <ForgotPasswordStep {...props} />
              </Animation>
              <Animation
                type="collapse"
                mountOnEnter
                unmountOnExit
                in={props.step === 'MFA'}
              >
                <MFAStep {...props} />
              </Animation>
              <Animation
                type="collapse"
                mountOnEnter
                unmountOnExit
                in={props.step === 'RECOVERY_CODE'}
              >
                <RecoveryCodeStep {...props} />
              </Animation>
            </FormContainer>

            <VinciWorksImage src="vinciworks.svg" />
            <MetaData>
              <VersionContainer>
                {t('login.version', {
                  version: process.env.REACT_APP_BUILD_NUMBER
                })}
              </VersionContainer>
              {/* <div>{t('login.copyline', { year: currentDateObject.y })}</div> */}
            </MetaData>
          </ContainerContent>
        </RelativeLoader>
      </Container>
      <ResetPasswordSuggestionDialog
        open={props.isResetPasswordSuggestionDialogOpen}
        title="login.resetPwd.dialog.title"
        content="login.resetPwd.dialog.content"
        handleConfirm={() => {
          props.onResetPasswordClicked();
          props.setNumOfUnsuccessfulTries(-9999);
        }}
        handleClose={
          () => props.setNumOfUnsuccessfulTries(-9999) // we don't want to show the dialog again
        }
        buttons={[
          { text: 'dialog.no', variant: 'outlined' },
          { text: 'dialog.yes', onClick: 'confirm' }
        ]}
      />
      <MultiFactorAuthSetupDialogEngine
        open={props.isMultiFactorAuthSetupDialogOpen}
        onClose={() => props.setIsMultiFactorAuthSetupDialogOpen(false)}
        onCompleteFirstMFALogin={props.onCompleteFirstMFALogin}
        userKey={props.multiFactorAuthSetupDialogUserKey}
        emailAddress={props.emailAddress}
        key={props.multiFactorAuthSetupDialogKey}
        oneTimeCodes={props.multiFactorAuthSetupDialogOneTimeCodes}
      />
      {/* <Button
        onClick={() => {
          props.setIsMultiFactorAuthSetupDialogOpen(true);
        }}
      >
        Show MFA Setup Dialog
      </Button> */}
    </Background>
  );
};

export default LoginPageView;

const EmailStep = props => {
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();

  return (
    <StepContainer>
      <SubTitleContainer>
        <h2>{t('pages.login.title')}</h2>
      </SubTitleContainer>
      <FormBox $mobileView={isMobileView}>
        <TextField
          autoFocus={props.step === STEPS.EMAIL}
          inputRef={input => (input ? (props.emailRef.current = input) : false)}
          value={props.emailAddress}
          onChange={props.onEmailFieldChange}
          onKeyDown={key =>
            props.onKeyPressed(key, props.onNextClicked, () => {
              props.setEmailAddressError(t('login.error.emptyEmail'));
              props.emailRef.current.focus();
            })
          }
          inputProps={{ 'data-lpignore': true }}
          data-testid="email-input"
          placeholder={t('login.email')}
          fullWidth
          error={!!props.emailAddressError}
          onFocus={() => props.setEmailAddressError('')}
          type={props.step != STEPS.EMAIL ? 'hidden' : 'text'}
        />
        <ErrorLine>
          <Animation in={!!props.emailAddressError} type="collapse">
            <ErrorIcon /> {props.emailAddressError}
          </Animation>
        </ErrorLine>
        <ButtonContainer>
          <Button
            data-testid="next"
            onClick={props.onNextClicked}
            customCSS={confirmBtnCss}
          >
            {t('login.button.next')}
          </Button>
        </ButtonContainer>
      </FormBox>
    </StepContainer>
  );
};

const PasswordStep = props => {
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();

  return (
    <StepContainer>
      <SubTitleContainer>
        <WelcomeH2>{t('login.welcome.title')}</WelcomeH2>
        <EmailButton
          onClick={props.onBack2EmailScrnClicked}
          startIcon={<ArrowBackIcon />}
        >
          {props.emailAddress}
        </EmailButton>
      </SubTitleContainer>
      <FormBox $mobileView={isMobileView}>
        <TextField
          inputRef={input =>
            input ? (props.passwordRef.current = input) : false
          }
          data-testid="password-input"
          placeholder={t('login.password')}
          fullWidth
          onFocus={() => props.setPwdError('')}
          error={!!props.pwdError}
          type={
            // props.step != STEPS.PASSWORD
            //   ? 'hidden'
            //   :
            props.showPassword ? 'text' : 'password'
          }
          value={props.password}
          onChange={props.onPwdFieldChanged}
          onKeyDown={key =>
            props.onKeyPressed(key, props.onLogin, () => {
              props.setPwdError(t('login.error.emptyPwd'));
              props.passwordRef.current.focus();
            })
          }
          inputProps={{ 'data-lpignore': true }}
          InputProps={{
            endAdornment: (
              <Tooltip
                arrow
                title={t(
                  `login.${props.showPassword ? 'hide' : 'show'}Password`
                )}
              >
                <IconButton
                  size="small"
                  onClick={() => props.setShowPassword(!props.showPassword)}
                >
                  {props.showPassword ? (
                    <VisibilityIcon />
                  ) : (
                    <VisibilityOffIcon />
                  )}
                </IconButton>
              </Tooltip>
            )
          }}
        />

        <ErrorLine>
          <Animation in={!!props.pwdError} type="collapse">
            <ErrorIcon /> {props.pwdError}
          </Animation>
        </ErrorLine>

        <ButtonContainer>
          <Button
            data-testid="log-in"
            customCSS={confirmBtnCss}
            onClick={props.onLogin}
          >
            {t('login.login')}
          </Button>
          <ButtonLink
            data-testid="reset-password"
            onClick={props.onResetPasswordClicked}
          >
            {t('login.dontknowpwd')}
          </ButtonLink>
        </ButtonContainer>
      </FormBox>
    </StepContainer>
  );
};

const MFAStep = props => {
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();

  return (
    <StepContainer>
      <SubTitleContainer>
        <WelcomeH2>{t('login.welcome.title')}</WelcomeH2>
        <EmailButton
          onClick={props.onBack2EmailScrnClicked}
          startIcon={<ArrowBackIcon />}
        >
          {props.emailAddress}
        </EmailButton>
      </SubTitleContainer>
      <FormBox $mobileView={isMobileView}>
        <MFATitleContainer $mobileView={isMobileView}>
          <h3>{t('login.multi_factor.title')}</h3>
          <h4>{t('login.multi_factor.instruction')}</h4>
        </MFATitleContainer>
      </FormBox>
      <FormBox $mobileView={isMobileView}>
        <MFATextField
          inputRef={input => (input ? (props.mfaRef.current = input) : false)}
          data-testid="mfa-input"
          placeholder={t('login.mfa')}
          fullWidth
          onFocus={() => props.setMfaError('')}
          error={!!props.mfaError}
          type="text"
          value={props.mfa}
          onChange={props.onMfaFieldChanged}
          onKeyDown={key =>
            props.onKeyPressed(key, props.onMFALogin, () => {
              props.setMfaError(t('login.error.emptyMfa'));
              props.mfaRef.current.focus();
            })
          }
          inputProps={{ 'data-lpignore': true }}
          variant="outlined"
          style={{ marginBottom: '2px' }}
        />

        <ErrorLine style={{ minHeight: '6px', marginBottom: '2px' }}>
          <Animation in={!!props.mfaError} type="collapse">
            <ErrorIcon /> {props.mfaError}
          </Animation>
        </ErrorLine>

        <ButtonContainer>
          {props.isRememberThisDeviceAllowed && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={props.isRememberThisDevice}
                  onChange={props.onToggleRememberThisDevice}
                />
              }
              label={
                <FormControlLabelText>
                  {t('login.multi_factor.remember_this_device')}
                </FormControlLabelText>
              }
            />
          )}

          <MFAInfoContainer>
            <StyledInfoIcon />
            <span>{t('login.no_recover_code_instructions')}</span>
          </MFAInfoContainer>

          <Button
            data-testid="log-in"
            customCSS={confirmBtnCss}
            onClick={props.onMFALogin}
          >
            {t('login.login')}
          </Button>
        </ButtonContainer>
      </FormBox>
    </StepContainer>
  );
};

const RecoveryCodeStep = props => {
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();

  return (
    <StepContainer>
      <SubTitleContainer>
        <WelcomeH2>{t('login.welcome.title')}</WelcomeH2>
        <EmailButton
          onClick={props.onBack2EmailScrnClicked}
          startIcon={<ArrowBackIcon />}
        >
          {props.emailAddress}
        </EmailButton>
      </SubTitleContainer>
      <MFATitleContainer $mobileView={isMobileView}>
        <h3>{t('login.multi_factor.title')}</h3>
        <h4>{t('login.multi_factor.recovery_instruction')}</h4>
      </MFATitleContainer>
      <FormBox $mobileView={isMobileView}>
        {/* <MFATextField
          inputRef={input =>
            input ? (props.mfaRecoveryRef.current = input) : false
          }
          data-testid="mfa-recovery-input"
          placeholder={t('login.mfa_recovery')}
          fullWidth
          onFocus={() => props.setMfaError('')}
          error={!!props.mfaError}
          type="text"
          value={props.mfaRecovery}
          onChange={props.onMfaRecoveryFieldChanged}
          onKeyDown={key =>
            props.onKeyPressed(key, props.onLogin, () => {
              props.setMfaError(t('login.error.emptyMfa'));
              props.mfaRecoveryRef.current.focus();
            })
          }
          inputProps={{ 'data-lpignore': true }}
          variant="outlined"
          onFocus={() => props.setMfaError('')}
          error={!!props.mfaError}
        /> */}

        <div>{t('login.no_recover_code_instructions')}</div>

        <ErrorLine>
          <Animation in={!!props.mfaError} type="collapse">
            <ErrorIcon /> {props.mfaError}
          </Animation>
        </ErrorLine>

        <ButtonContainer>
          <ButtonLink
            data-testid="back-to-app-code"
            onClick={props.onBackToAppCodeClicked}
            $widthAuto
          >
            {t('login.multi_factor.back_to_app_code')}
          </ButtonLink>

          <Button
            data-testid="log-in"
            customCSS={confirmBtnCss}
            onClick={props.onMFALogin}
          >
            {t('login.login')}
          </Button>
        </ButtonContainer>
      </FormBox>
    </StepContainer>
  );
};

const ForgotPasswordStep = props => {
  const t = useTranslations();

  return (
    <StepContainer $justify="center">
      {t('login.resetPwd.1')}
      <EmailContainer>{props.emailAddress || 'umail@gmail.con'}</EmailContainer>
      {t('login.resetPwd.2')}
      <small>{t('login.resetPwd.3')}</small>
    </StepContainer>
  );
};

const SetPasswordStep = props => {
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();

  return (
    <StepContainer>
      <SubTitleContainer>
        <h2>{t('login.setPwd.title')}</h2>
      </SubTitleContainer>
      <FormBox $mobileView={isMobileView}>
        <TextField
          $strength={props.passwordStrengthMeter}
          autoFocus={props.step === STEPS.SET_PASSWORD}
          inputRef={input =>
            input ? (props.newPasswordRef.current = input) : false
          }
          value={props.newPassword}
          onChange={props.onSetNewPasswordInputChange}
          id="set-password-input"
          data-testid="set-password-input"
          placeholder={t('login.setPwd.input.placeholder')}
          fullWidth
          error={!!props.newPwdError}
          onFocus={() => props.setNewPwdError('')}
          type={props.showNewPassword ? 'text' : 'password'}
          onKeyDown={key =>
            props.onKeyPressed(key, props.onPasswordSet, () => {
              props.setNewPwdError(t('login.error.emptyPwd'));
              props.newPasswordRef.current.focus();
            })
          }
          inputProps={{ 'data-lpignore': true }}
          InputProps={{
            endAdornment: (
              <Tooltip
                arrow
                title={t(
                  `login.${props.showNewPassword ? 'hide' : 'show'}Password`
                )}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    props.setShowNewPassword(!props.showNewPassword)
                  }
                >
                  {props.showNewPassword ? (
                    <VisibilityIcon />
                  ) : (
                    <VisibilityOffIcon />
                  )}
                </IconButton>
              </Tooltip>
            )
          }}
        />
        <StrengthMeter value={props.passwordStrengthMeter} />
        <ErrorLine>
          <Animation in={!!props.newPwdError} type="collapse">
            <ErrorIcon /> {props.newPwdError}
          </Animation>
        </ErrorLine>

        <ButtonContainer>
          <SetPwdReq>{t('login.setPwd.req')}</SetPwdReq>
          <Button
            data-testid="confirm-set-password"
            customCSS={confirmBtnCss}
            onClick={props.onPasswordSet}
          >
            {t('login.setPwd.button')}
          </Button>
        </ButtonContainer>
      </FormBox>
    </StepContainer>
  );
};

const ButtonPositioner = styled.div`
  margin-top: 2rem;
`;
const ActivateAccountStep = props => {
  const t = useTranslations();

  return (
    <StepContainer $justify="center">
      {t('login.activate-account.text')}
      <ButtonPositioner>
        <Button onClick={props.onAccountActivate}>
          {t('login.activate-account.button')}
        </Button>
      </ButtonPositioner>
    </StepContainer>
  );
};
